import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AppBrandLogo } from "@/components/brand/AppBrandLogo";
import { LanguageSwitcher } from "@/i18n/components/LanguageSwitcher";
import { AppThemeToggle } from "./AppThemeToggle";

export interface AuthShellProps {
  /** Form column content (title, form, links). */
  children: ReactNode;
  /** Optional muted content under the form (copyright, legal links). */
  footer?: ReactNode;
}

/**
 * Split-screen frame for unauthenticated pages: brand panel on the left (lg+),
 * centered form column with language + theme controls on the right.
 */
export function AuthShell({ children, footer }: AuthShellProps) {
  const { t } = useTranslation("auth");

  return (
    <div className="flex min-h-screen bg-surface-0 dark:bg-surface-50">
      <AuthBrandPanel
        tagline={t("partnerPortal")}
        titleLines={[t("heroTitleLine1"), t("heroTitleLine2")]}
        subtitle={t("heroSubtitle")}
        stats={[
          { value: "500+", label: t("statPartners") },
          { value: "10K+", label: t("statBookings") },
        ]}
      />

      <div className="flex min-w-0 flex-1 flex-col px-5 py-5 sm:px-8">
        <div className="flex items-center justify-between gap-3">
          <div className="h-8 w-24 lg:invisible">
            <AppBrandLogo background="light" className="h-full w-full" />
          </div>
          <div className="flex items-center gap-2" data-skip-navigation-guard>
            <LanguageSwitcher variant="sidebar" />
            <AppThemeToggle />
          </div>
        </div>

        <div className="flex flex-1 items-center justify-center py-10">
          <div className="w-full max-w-[380px] animate-slide-up">{children}</div>
        </div>

        {footer ? (
          <div className="text-center text-xs text-surface-500">{footer}</div>
        ) : null}
      </div>
    </div>
  );
}

function AuthBrandPanel({
  tagline,
  titleLines,
  subtitle,
  stats,
}: {
  tagline: string;
  titleLines: readonly string[];
  subtitle: string;
  stats: ReadonlyArray<{ value: string; label: string }>;
}) {
  return (
    <div className="relative hidden w-[46%] max-w-[760px] shrink-0 overflow-hidden bg-[#06151d] lg:flex">
      {/* Ocean glow + sun glow + fine grid */}
      <div className="pointer-events-none absolute inset-0" aria-hidden>
        <div className="absolute -left-40 -top-40 h-[560px] w-[560px] rounded-full bg-primary-500/35 blur-[120px]" />
        <div className="absolute -bottom-48 right-[-120px] h-[520px] w-[520px] rounded-full bg-accent-500/25 blur-[120px]" />
        <div className="absolute left-1/3 top-1/2 h-[380px] w-[380px] rounded-full bg-[#74d8ec]/10 blur-[100px]" />
        <div
          className="absolute inset-0 opacity-[0.18]"
          style={{
            backgroundImage:
              "linear-gradient(rgb(255 255 255 / 0.08) 1px, transparent 1px), linear-gradient(90deg, rgb(255 255 255 / 0.08) 1px, transparent 1px)",
            backgroundSize: "44px 44px",
            maskImage:
              "radial-gradient(ellipse 80% 70% at 30% 40%, black 40%, transparent 100%)",
          }}
        />
      </div>

      <div className="relative z-10 flex w-full flex-col justify-between p-12 text-white xl:p-16">
        <div className="h-12 w-36">
          <AppBrandLogo background="dark" className="h-full w-full" />
        </div>

        <div className="max-w-lg">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-medium text-white/80 backdrop-blur">
            <span className="h-1.5 w-1.5 rounded-full bg-accent-400 shadow-[0_0_8px_2px_rgb(255_160_32/0.6)]" />
            {tagline}
          </span>
          <h2 className="mt-6 text-balance text-4xl font-semibold leading-[1.1] tracking-tight xl:text-5xl">
            {titleLines.map((line, index) => (
              <span
                key={line}
                className={
                  index === titleLines.length - 1
                    ? "block bg-gradient-to-r from-[#aceaf5] via-[#74d8ec] to-[#ffb43a] bg-clip-text text-transparent"
                    : "block"
                }
              >
                {line}
              </span>
            ))}
          </h2>
          <p className="mt-5 max-w-md text-pretty text-base leading-relaxed text-white/65">
            {subtitle}
          </p>
        </div>

        <div className="grid max-w-md grid-cols-2 gap-3">
          {stats.map((stat) => (
            <div
              key={stat.label}
              className="rounded-xl border border-white/10 bg-white/[0.04] p-4 backdrop-blur-md"
            >
              <p className="text-2xl font-semibold tabular-nums tracking-tight">
                {stat.value}
              </p>
              <p className="mt-0.5 text-xs text-white/55">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
